export const DateHelpers = {
  getActualYearAndMonth,
  getFullDate,
  getFullDateWithTime,
  getStringDiffFromNow,
  getDiffFromNow,
  checkIsFirstRecruitmentMember,
  formatFullDateHours,
  isCurrentDateInRange,
  getStringDeadlineTime,
};

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY  = MS_PER_HOUR * 24;

/**
 * Zwraca aktualny rok i miesiąc w formacie 'YYYY-MM'
 * @returns {string}
 */
function getActualYearAndMonth() {
  let currentDate = new Date();
  let year = currentDate.getFullYear();
  let month = (currentDate.getMonth() + 1).toString().padStart(2, "0");

  return `${year}-${month}`;
}

/**
 * Returns a days the difference from now to a given future date.
 *
 * @param {Date} date - The future date to compare with the current time.
 *
 * @returns {string} Days differenece from given date.
 */
function getDiffFromNow(date) {
  const toDay = new Date();
  const diffDaysFromCreation = Math.ceil((date - toDay) / (1000 * 60 * 60 * 24)) - 1;

  return diffDaysFromCreation;
}

/**
 * Returns a human-readable string representing the time difference from now to a given future date.
 *
 * @param {Date} date - The future date to compare with the current time.
 *
 * @returns {string} A string in Polish indicating the number of days or hours until the date (e.g., "3 dni", "5 godzin"), or an empty string if the date is not in the future.
 */
function getStringDiffFromNow(date) {
  const toDay = new Date();
  const diffDaysFromCreation = getDiffFromNow(date);

  if (diffDaysFromCreation === 1) {
    return `${diffDaysFromCreation} dzień`;
  }

  if (diffDaysFromCreation > 0) {
    return `${diffDaysFromCreation} dni`;
  }

  if (diffDaysFromCreation === 0) {
    const diffHoursFromCreation = Math.ceil((date - toDay) / (1000 * 60 * 60));

    return diffHoursFromCreation > 0 ? `${diffHoursFromCreation} godzin` : '';
  }

  return '';
}

/**
 * Zwraca aktualny rok, miesiąc i dzień w formacie 'YYYY-MM-DD'
 * @returns {string}
 */
function getFullDate() {
  let currentDate = new Date();
  let year = currentDate.getFullYear();
  let month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
  let day = currentDate.getDate().toString().padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * Zwraca aktualny rok, miesiąc i dzień w formacie 'YYYY-MM-DD HH-MM'
 * @returns {string}
 */
function formatFullDateHours(string) {
  if(!string) return;
  let date = new Date(string);
  let year = date.getFullYear();
  let month = (date.getMonth() + 1).toString().padStart(2, "0");
  let day = date.getDate().toString().padStart(2, "0");
  let hours = String(date.getHours()).padStart(2, '0');
  let minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/**
 * Zwraca aktualny rok, miesiąc i dzień godziny i sekundy jesli parametr withSeconds jest - true
 * w formacie 'YYYY-MM-DDTHH:II:SS'
 * @param {bool} withSeconds
 * @returns {string}
 */
function getFullDateWithTime(withSeconds = false) {
  let currentDate = new Date();
  let day = currentDate.getDate().toString().padStart(2, "0");
  let month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
  let year = currentDate.getFullYear();
  let hours = currentDate.getHours().toString().padStart(2, "0");
  let minutes = currentDate.getMinutes().toString().padStart(2, "0");
  let seconds = currentDate.getSeconds().toString().padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}` + withSeconds
    ? ":" + seconds
    : "";
}

function checkIsFirstRecruitmentMember(num) {
  return parseInt(num) === 1 ? true : false;
}

/**
 * Checks if the current date and time falls within the specified date range (inclusive).
 *
 * @param {string} from The start date/time of the range in a format parseable by Date constructor.
 * @param {string} to The end date/time of the range in a format parseable by Date constructor.
 * @returns {boolean} Returns true if the current date/time is between or equal to the from and to dates; otherwise, false.
 */
function isCurrentDateInRange(from, to) {
  const now = new Date();
  const fromDate = new Date(from);
  const toDate = new Date(to);

  return now >= fromDate && now <= toDate;
}

/**
 * Returns a human-readable string representing the time difference from now to a given deadline
 *
 * @param {Date|string} dateInput - Deadline to compare with the current time.
 *
 * @returns {string} A string in Polish indicating the number of days or hours until deadline (e.g., "3 dni", "5 godzin"), or an empty string if the date is not in the future.
 */
function getStringDeadlineTime(dateInput) {
  const now = new Date();
  const deadline = dateInput instanceof Date ? dateInput : new Date(dateInput);

  const diffMs = deadline.getTime() - now.getTime();

  if (diffMs <= 0) {
    return '';
  }

  if (diffMs < MS_PER_DAY) {
    const hours = Math.ceil(diffMs / MS_PER_HOUR);
    return pluralizePL(hours, ['godzina', 'godziny', 'godzin']);
  } else {
    const days = Math.ceil(diffMs / MS_PER_DAY);
    return pluralizePL(days, ['dzień', 'dni', 'dni']);
  }
}

function pluralizePL(n, [one, few, many]) {
  n = Math.abs(n);

  if (n % 10 === 1 && n % 100 !== 11) {
    return `${n} ${one}`;
  }

  if ([2,3,4].includes(n % 10) && ![12,13,14].includes(n % 100)) {
    return `${n} ${few}`;
  } 

  return `${n} ${many}`;
}
